import React, { useCallback } from 'react';
import useStore from '~/store';
import { ToastProps } from '~/store/slices/toast/types';
import Toast, { type ToastTone } from '../Toast';
import { green, orange, red } from '~/styles/tokens/primitives.colors';
import { ToastContainerProps } from './types';
import * as S from './styles';
import { IoAlertCircle, IoCheckmarkCircle, IoWarning } from 'react-icons/io5';

/**
 * Tons fixos por tipo (Figma 2110:3605): faixa clara + texto escuro da mesma família. Não seguem o modo escuro
 * de propósito — o contraste da faixa clara fica garantido sobre qualquer fundo.
 */
const TOAST_TYPES: Record<
  ToastProps['type'],
  { icon: typeof IoAlertCircle; tone: ToastTone; role: 'alert' | 'status' }
> = {
  success: {
    icon: IoCheckmarkCircle,
    role: 'status',
    tone: { accent: green[900], background: green[100], border: green[300], text: green[1100] }
  },
  error: {
    icon: IoAlertCircle,
    role: 'alert',
    tone: { accent: red[800], background: red[100], border: red[200], text: red[900] }
  },
  warning: {
    icon: IoWarning,
    role: 'alert',
    tone: { accent: orange[800], background: orange[100], border: orange[300], text: orange[1100] }
  }
};

const ToastContainer: React.FC<ToastContainerProps> = ({ position = 'bottom-center', autoClose = 5000 }) => {
  const { toasts, removeToast } = useStore((store) => ({
    toasts: store.toasts as ToastProps[],
    removeToast: store.removeToast
  }));

  const handleDismissToast = useCallback(
    (id: string) => {
      removeToast(id);
    },
    [removeToast]
  );

  return (
    <S.ListContainer data-app-id="app-toast-container" position={position}>
      {toasts.map((toast) => (
        <Toast
          title={toast.title}
          message={toast.message}
          icon={TOAST_TYPES[toast.type].icon}
          tone={TOAST_TYPES[toast.type].tone}
          role={TOAST_TYPES[toast.type].role}
          dismiss={() => handleDismissToast(toast.id)}
          key={toast.id}
          autoClose={autoClose}
        />
      ))}
    </S.ListContainer>
  );
};

export default ToastContainer;
